/**
 * Semilla de arranque del lector OFFLINE — King James Version (dominio público).
 *
 * Subconjunto de arranque: Génesis 1–3 + Juan 1–3. Es suficiente para que el
 * lector funcione sin servidor (navegación libro → capítulo → versículo y
 * renderizado del texto) desde el primer arranque. El catálogo completo puede
 * sincronizarse después vía el stream PowerSync `bible_content`.
 *
 * Fuente: King James Version (1769), de dominio público.
 *
 * `book` usa el id numérico canónico de `@/utils/bible-books-data`
 * (Génesis = 1, Juan = 43).
 *
 * Este módulo es DATA (texto bíblico de dominio público), no cadenas de UI: el
 * versículo se renderiza tal cual desde la base local. Por eso se desactiva
 * `lingui/no-unlocalized-strings` a nivel de archivo (no hay nada que traducir).
 */
/* eslint-disable lingui/no-unlocalized-strings */

export const SEED_VERSION = "kjv";

/**
 * Marcador de generación de la siembra del LECTOR. Se persiste en localStorage
 * tras sembrar; al subirlo forzamos una re-siembra aunque el conteo de filas no
 * cambie (p. ej. cuando cambia el CONTENIDO de un versículo —marcas Strong— pero
 * no su cantidad). Súbelo cuando edites el texto/marcas de la semilla.
 */
export const SEED_GENERATION = "2";

interface SeedVerse {
  book: number;
  chapter: number;
  verse: number;
  text: string;
}

export const KJV_SEED_VERSES: SeedVerse[] = [
  // ── Genesis 1 ──────────────────────────────────────────────────────────
  { book: 1, chapter: 1, verse: 1, text: "In the beginning God created the heaven and the earth." },
  {
    book: 1,
    chapter: 1,
    verse: 2,
    text: "And the earth was without form, and void; and darkness was upon the face of the deep. And the Spirit of God moved upon the face of the waters.",
  },
  { book: 1, chapter: 1, verse: 3, text: "And God said, Let there be light: and there was light." },
  {
    book: 1,
    chapter: 1,
    verse: 4,
    text: "And God saw the light, that it was good: and God divided the light from the darkness.",
  },
  {
    book: 1,
    chapter: 1,
    verse: 5,
    text: "And God called the light Day, and the darkness he called Night. And the evening and the morning were the first day.",
  },
  {
    book: 1,
    chapter: 1,
    verse: 6,
    text: "And God said, Let there be a firmament in the midst of the waters, and let it divide the waters from the waters.",
  },
  {
    book: 1,
    chapter: 1,
    verse: 7,
    text: "And God made the firmament, and divided the waters which were under the firmament from the waters which were above the firmament: and it was so.",
  },
  {
    book: 1,
    chapter: 1,
    verse: 8,
    text: "And God called the firmament Heaven. And the evening and the morning were the second day.",
  },
  {
    book: 1,
    chapter: 1,
    verse: 9,
    text: "And God said, Let the waters under the heaven be gathered together unto one place, and let the dry land appear: and it was so.",
  },
  {
    book: 1,
    chapter: 1,
    verse: 10,
    text: "And God called the dry land Earth; and the gathering together of the waters called he Seas: and God saw that it was good.",
  },
  {
    book: 1,
    chapter: 1,
    verse: 11,
    text: "And God said, Let the earth bring forth grass, the herb yielding seed, and the fruit tree yielding fruit after his kind, whose seed is in itself, upon the earth: and it was so.",
  },
  {
    book: 1,
    chapter: 1,
    verse: 12,
    text: "And the earth brought forth grass, and herb yielding seed after his kind, and the tree yielding fruit, whose seed was in itself, after his kind: and God saw that it was good.",
  },
  { book: 1, chapter: 1, verse: 13, text: "And the evening and the morning were the third day." },
  {
    book: 1,
    chapter: 1,
    verse: 14,
    text: "And God said, Let there be lights in the firmament of the heaven to divide the day from the night; and let them be for signs, and for seasons, and for days, and years:",
  },
  {
    book: 1,
    chapter: 1,
    verse: 15,
    text: "And let them be for lights in the firmament of the heaven to give light upon the earth: and it was so.",
  },
  {
    book: 1,
    chapter: 1,
    verse: 16,
    text: "And God made two great lights; the greater light to rule the day, and the lesser light to rule the night: he made the stars also.",
  },
  {
    book: 1,
    chapter: 1,
    verse: 17,
    text: "And God set them in the firmament of the heaven to give light upon the earth,",
  },
  {
    book: 1,
    chapter: 1,
    verse: 18,
    text: "And to rule over the day and over the night, and to divide the light from the darkness: and God saw that it was good.",
  },
  { book: 1, chapter: 1, verse: 19, text: "And the evening and the morning were the fourth day." },
  {
    book: 1,
    chapter: 1,
    verse: 20,
    text: "And God said, Let the waters bring forth abundantly the moving creature that hath life, and fowl that may fly above the earth in the open firmament of heaven.",
  },
  {
    book: 1,
    chapter: 1,
    verse: 21,
    text: "And God created great whales, and every living creature that moveth, which the waters brought forth abundantly, after their kind, and every winged fowl after his kind: and God saw that it was good.",
  },
  {
    book: 1,
    chapter: 1,
    verse: 22,
    text: "And God blessed them, saying, Be fruitful, and multiply, and fill the waters in the seas, and let fowl multiply in the earth.",
  },
  { book: 1, chapter: 1, verse: 23, text: "And the evening and the morning were the fifth day." },
  {
    book: 1,
    chapter: 1,
    verse: 24,
    text: "And God said, Let the earth bring forth the living creature after his kind, cattle, and creeping thing, and beast of the earth after his kind: and it was so.",
  },
  {
    book: 1,
    chapter: 1,
    verse: 25,
    text: "And God made the beast of the earth after his kind, and cattle after their kind, and every thing that creepeth upon the earth after his kind: and God saw that it was good.",
  },
  {
    book: 1,
    chapter: 1,
    verse: 26,
    text: "And God said, Let us make man in our image, after our likeness: and let them have dominion over the fish of the sea, and over the fowl of the air, and over the cattle, and over all the earth, and over every creeping thing that creepeth upon the earth.",
  },
  {
    book: 1,
    chapter: 1,
    verse: 27,
    text: "So God created man in his own image, in the image of God created he him; male and female created he them.",
  },
  {
    book: 1,
    chapter: 1,
    verse: 28,
    text: "And God blessed them, and God said unto them, Be fruitful, and multiply, and replenish the earth, and subdue it: and have dominion over the fish of the sea, and over the fowl of the air, and over every living thing that moveth upon the earth.",
  },
  {
    book: 1,
    chapter: 1,
    verse: 29,
    text: "And God said, Behold, I have given you every herb bearing seed, which is upon the face of all the earth, and every tree, in the which is the fruit of a tree yielding seed; to you it shall be for meat.",
  },
  {
    book: 1,
    chapter: 1,
    verse: 30,
    text: "And to every beast of the earth, and to every fowl of the air, and to every thing that creepeth upon the earth, wherein there is life, I have given every green herb for meat: and it was so.",
  },
  {
    book: 1,
    chapter: 1,
    verse: 31,
    text: "And God saw every thing that he had made, and, behold, it was very good. And the evening and the morning were the sixth day.",
  },

  // ── Genesis 2 ──────────────────────────────────────────────────────────
  {
    book: 1,
    chapter: 2,
    verse: 1,
    text: "Thus the heavens and the earth were finished, and all the host of them.",
  },
  {
    book: 1,
    chapter: 2,
    verse: 2,
    text: "And on the seventh day God ended his work which he had made; and he rested on the seventh day from all his work which he had made.",
  },
  {
    book: 1,
    chapter: 2,
    verse: 3,
    text: "And God blessed the seventh day, and sanctified it: because that in it he had rested from all his work which God created and made.",
  },
  {
    book: 1,
    chapter: 2,
    verse: 4,
    text: "These are the generations of the heavens and of the earth when they were created, in the day that the LORD God made the earth and the heavens,",
  },
  {
    book: 1,
    chapter: 2,
    verse: 5,
    text: "And every plant of the field before it was in the earth, and every herb of the field before it grew: for the LORD God had not caused it to rain upon the earth, and there was not a man to till the ground.",
  },
  {
    book: 1,
    chapter: 2,
    verse: 6,
    text: "But there went up a mist from the earth, and watered the whole face of the ground.",
  },
  {
    book: 1,
    chapter: 2,
    verse: 7,
    text: "And the LORD God formed man of the dust of the ground, and breathed into his nostrils the breath of life; and man became a living soul.",
  },
  {
    book: 1,
    chapter: 2,
    verse: 8,
    text: "And the LORD God planted a garden eastward in Eden; and there he put the man whom he had formed.",
  },
  {
    book: 1,
    chapter: 2,
    verse: 9,
    text: "And out of the ground made the LORD God to grow every tree that is pleasant to the sight, and good for food; the tree of life also in the midst of the garden, and the tree of knowledge of good and evil.",
  },
  {
    book: 1,
    chapter: 2,
    verse: 10,
    text: "And a river went out of Eden to water the garden; and from thence it was parted, and became into four heads.",
  },
  {
    book: 1,
    chapter: 2,
    verse: 11,
    text: "The name of the first is Pison: that is it which compasseth the whole land of Havilah, where there is gold;",
  },
  {
    book: 1,
    chapter: 2,
    verse: 12,
    text: "And the gold of that land is good: there is bdellium and the onyx stone.",
  },
  {
    book: 1,
    chapter: 2,
    verse: 13,
    text: "And the name of the second river is Gihon: the same is it that compasseth the whole land of Ethiopia.",
  },
  {
    book: 1,
    chapter: 2,
    verse: 14,
    text: "And the name of the third river is Hiddekel: that is it which goeth toward the east of Assyria. And the fourth river is Euphrates.",
  },
  {
    book: 1,
    chapter: 2,
    verse: 15,
    text: "And the LORD God took the man, and put him into the garden of Eden to dress it and to keep it.",
  },
  {
    book: 1,
    chapter: 2,
    verse: 16,
    text: "And the LORD God commanded the man, saying, Of every tree of the garden thou mayest freely eat:",
  },
  {
    book: 1,
    chapter: 2,
    verse: 17,
    text: "But of the tree of the knowledge of good and evil, thou shalt not eat of it: for in the day that thou eatest thereof thou shalt surely die.",
  },
  {
    book: 1,
    chapter: 2,
    verse: 18,
    text: "And the LORD God said, It is not good that the man should be alone; I will make him an help meet for him.",
  },
  {
    book: 1,
    chapter: 2,
    verse: 19,
    text: "And out of the ground the LORD God formed every beast of the field, and every fowl of the air; and brought them unto Adam to see what he would call them: and whatsoever Adam called every living creature, that was the name thereof.",
  },
  {
    book: 1,
    chapter: 2,
    verse: 20,
    text: "And Adam gave names to all cattle, and to the fowl of the air, and to every beast of the field; but for Adam there was not found an help meet for him.",
  },
  {
    book: 1,
    chapter: 2,
    verse: 21,
    text: "And the LORD God caused a deep sleep to fall upon Adam, and he slept: and he took one of his ribs, and closed up the flesh instead thereof;",
  },
  {
    book: 1,
    chapter: 2,
    verse: 22,
    text: "And the rib, which the LORD God had taken from man, made he a woman, and brought her unto the man.",
  },
  {
    book: 1,
    chapter: 2,
    verse: 23,
    text: "And Adam said, This is now bone of my bones, and flesh of my flesh: she shall be called Woman, because she was taken out of Man.",
  },
  {
    book: 1,
    chapter: 2,
    verse: 24,
    text: "Therefore shall a man leave his father and his mother, and shall cleave unto his wife: and they shall be one flesh.",
  },
  {
    book: 1,
    chapter: 2,
    verse: 25,
    text: "And they were both naked, the man and his wife, and were not ashamed.",
  },

  // ── Genesis 3 ──────────────────────────────────────────────────────────
  {
    book: 1,
    chapter: 3,
    verse: 1,
    text: "Now the serpent was more subtil than any beast of the field which the LORD God had made. And he said unto the woman, Yea, hath God said, Ye shall not eat of every tree of the garden?",
  },
  {
    book: 1,
    chapter: 3,
    verse: 2,
    text: "And the woman said unto the serpent, We may eat of the fruit of the trees of the garden:",
  },
  {
    book: 1,
    chapter: 3,
    verse: 3,
    text: "But of the fruit of the tree which is in the midst of the garden, God hath said, Ye shall not eat of it, neither shall ye touch it, lest ye die.",
  },
  {
    book: 1,
    chapter: 3,
    verse: 4,
    text: "And the serpent said unto the woman, Ye shall not surely die:",
  },
  {
    book: 1,
    chapter: 3,
    verse: 5,
    text: "For God doth know that in the day ye eat thereof, then your eyes shall be opened, and ye shall be as gods, knowing good and evil.",
  },
  {
    book: 1,
    chapter: 3,
    verse: 6,
    text: "And when the woman saw that the tree was good for food, and that it was pleasant to the eyes, and a tree to be desired to make one wise, she took of the fruit thereof, and did eat, and gave also unto her husband with her; and he did eat.",
  },
  {
    book: 1,
    chapter: 3,
    verse: 7,
    text: "And the eyes of them both were opened, and they knew that they were naked; and they sewed fig leaves together, and made themselves aprons.",
  },
  {
    book: 1,
    chapter: 3,
    verse: 8,
    text: "And they heard the voice of the LORD God walking in the garden in the cool of the day: and Adam and his wife hid themselves from the presence of the LORD God amongst the trees of the garden.",
  },
  {
    book: 1,
    chapter: 3,
    verse: 9,
    text: "And the LORD God called unto Adam, and said unto him, Where art thou?",
  },
  {
    book: 1,
    chapter: 3,
    verse: 10,
    text: "And he said, I heard thy voice in the garden, and I was afraid, because I was naked; and I hid myself.",
  },
  {
    book: 1,
    chapter: 3,
    verse: 11,
    text: "And he said, Who told thee that thou wast naked? Hast thou eaten of the tree, whereof I commanded thee that thou shouldest not eat?",
  },
  {
    book: 1,
    chapter: 3,
    verse: 12,
    text: "And the man said, The woman whom thou gavest to be with me, she gave me of the tree, and I did eat.",
  },
  {
    book: 1,
    chapter: 3,
    verse: 13,
    text: "And the LORD God said unto the woman, What is this that thou hast done? And the woman said, The serpent beguiled me, and I did eat.",
  },
  {
    book: 1,
    chapter: 3,
    verse: 14,
    text: "And the LORD God said unto the serpent, Because thou hast done this, thou art cursed above all cattle, and above every beast of the field; upon thy belly shalt thou go, and dust shalt thou eat all the days of thy life:",
  },
  {
    book: 1,
    chapter: 3,
    verse: 15,
    text: "And I will put enmity between thee and the woman, and between thy seed and her seed; it shall bruise thy head, and thou shalt bruise his heel.",
  },
  {
    book: 1,
    chapter: 3,
    verse: 16,
    text: "Unto the woman he said, I will greatly multiply thy sorrow and thy conception; in sorrow thou shalt bring forth children; and thy desire shall be to thy husband, and he shall rule over thee.",
  },
  {
    book: 1,
    chapter: 3,
    verse: 17,
    text: "And unto Adam he said, Because thou hast hearkened unto the voice of thy wife, and hast eaten of the tree, of which I commanded thee, saying, Thou shalt not eat of it: cursed is the ground for thy sake; in sorrow shalt thou eat of it all the days of thy life;",
  },
  {
    book: 1,
    chapter: 3,
    verse: 18,
    text: "Thorns also and thistles shall it bring forth to thee; and thou shalt eat the herb of the field;",
  },
  {
    book: 1,
    chapter: 3,
    verse: 19,
    text: "In the sweat of thy face shalt thou eat bread, till thou return unto the ground; for out of it wast thou taken: for dust thou art, and unto dust shalt thou return.",
  },
  {
    book: 1,
    chapter: 3,
    verse: 20,
    text: "And Adam called his wife's name Eve; because she was the mother of all living.",
  },
  {
    book: 1,
    chapter: 3,
    verse: 21,
    text: "Unto Adam also and to his wife did the LORD God make coats of skins, and clothed them.",
  },
  {
    book: 1,
    chapter: 3,
    verse: 22,
    text: "And the LORD God said, Behold, the man is become as one of us, to know good and evil: and now, lest he put forth his hand, and take also of the tree of life, and eat, and live for ever:",
  },
  {
    book: 1,
    chapter: 3,
    verse: 23,
    text: "Therefore the LORD God sent him forth from the garden of Eden, to till the ground from whence he was taken.",
  },
  {
    book: 1,
    chapter: 3,
    verse: 24,
    text: "So he drove out the man; and he placed at the east of the garden of Eden Cherubims, and a flaming sword which turned every way, to keep the way of the tree of life.",
  },

  // ── John 1 ─────────────────────────────────────────────────────────────
  {
    book: 43,
    chapter: 1,
    verse: 1,
    text: "In the beginning was the Word, and the Word was with God, and the Word was God.",
  },
  { book: 43, chapter: 1, verse: 2, text: "The same was in the beginning with God." },
  {
    book: 43,
    chapter: 1,
    verse: 3,
    text: "All things were made by him; and without him was not any thing made that was made.",
  },
  { book: 43, chapter: 1, verse: 4, text: "In him was life; and the life was the light of men." },
  {
    book: 43,
    chapter: 1,
    verse: 5,
    text: "And the light shineth in darkness; and the darkness comprehended it not.",
  },
  { book: 43, chapter: 1, verse: 6, text: "There was a man sent from God, whose name was John." },
  {
    book: 43,
    chapter: 1,
    verse: 7,
    text: "The same came for a witness, to bear witness of the Light, that all men through him might believe.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 8,
    text: "He was not that Light, but was sent to bear witness of that Light.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 9,
    text: "That was the true Light, which lighteth every man that cometh into the world.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 10,
    text: "He was in the world, and the world was made by him, and the world knew him not.",
  },
  { book: 43, chapter: 1, verse: 11, text: "He came unto his own, and his own received him not." },
  {
    book: 43,
    chapter: 1,
    verse: 12,
    text: "But as many as received him, to them gave he power to become the sons of God, even to them that believe on his name:",
  },
  {
    book: 43,
    chapter: 1,
    verse: 13,
    text: "Which were born, not of blood, nor of the will of the flesh, nor of the will of man, but of God.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 14,
    text: "And the Word was made flesh, and dwelt among us, (and we beheld his glory, the glory as of the only begotten of the Father,) full of grace and truth.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 15,
    text: "John bare witness of him, and cried, saying, This was he of whom I spake, He that cometh after me is preferred before me: for he was before me.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 16,
    text: "And of his fulness have all we received, and grace for grace.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 17,
    text: "For the law was given by Moses, but grace and truth came by Jesus Christ.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 18,
    text: "No man hath seen God at any time; the only begotten Son, which is in the bosom of the Father, he hath declared him.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 19,
    text: "And this is the record of John, when the Jews sent priests and Levites from Jerusalem to ask him, Who art thou?",
  },
  {
    book: 43,
    chapter: 1,
    verse: 20,
    text: "And he confessed, and denied not; but confessed, I am not the Christ.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 21,
    text: "And they asked him, What then? Art thou Elias? And he saith, I am not. Art thou that prophet? And he answered, No.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 22,
    text: "Then said they unto him, Who art thou? that we may give an answer to them that sent us. What sayest thou of thyself?",
  },
  {
    book: 43,
    chapter: 1,
    verse: 23,
    text: "He said, I am the voice of one crying in the wilderness, Make straight the way of the Lord, as said the prophet Esaias.",
  },
  { book: 43, chapter: 1, verse: 24, text: "And they which were sent were of the Pharisees." },
  {
    book: 43,
    chapter: 1,
    verse: 25,
    text: "And they asked him, and said unto him, Why baptizest thou then, if thou be not that Christ, nor Elias, neither that prophet?",
  },
  {
    book: 43,
    chapter: 1,
    verse: 26,
    text: "John answered them, saying, I baptize with water: but there standeth one among you, whom ye know not;",
  },
  {
    book: 43,
    chapter: 1,
    verse: 27,
    text: "He it is, who coming after me is preferred before me, whose shoe's latchet I am not worthy to unloose.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 28,
    text: "These things were done in Bethabara beyond Jordan, where John was baptizing.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 29,
    text: "The next day John seeth Jesus coming unto him, and saith, Behold the Lamb of God, which taketh away the sin of the world.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 30,
    text: "This is he of whom I said, After me cometh a man which is preferred before me: for he was before me.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 31,
    text: "And I knew him not: but that he should be made manifest to Israel, therefore am I come baptizing with water.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 32,
    text: "And John bare record, saying, I saw the Spirit descending from heaven like a dove, and it abode upon him.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 33,
    text: "And I knew him not: but he that sent me to baptize with water, the same said unto me, Upon whom thou shalt see the Spirit descending, and remaining on him, the same is he which baptizeth with the Holy Ghost.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 34,
    text: "And I saw, and bare record that this is the Son of God.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 35,
    text: "Again the next day after John stood, and two of his disciples;",
  },
  {
    book: 43,
    chapter: 1,
    verse: 36,
    text: "And looking upon Jesus as he walked, he saith, Behold the Lamb of God!",
  },
  {
    book: 43,
    chapter: 1,
    verse: 37,
    text: "And the two disciples heard him speak, and they followed Jesus.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 38,
    text: "Then Jesus turned, and saw them following, and saith unto them, What seek ye? They said unto him, Rabbi, (which is to say, being interpreted, Master,) where dwellest thou?",
  },
  {
    book: 43,
    chapter: 1,
    verse: 39,
    text: "He saith unto them, Come and see. They came and saw where he dwelt, and abode with him that day: for it was about the tenth hour.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 40,
    text: "One of the two which heard John speak, and followed him, was Andrew, Simon Peter's brother.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 41,
    text: "He first findeth his own brother Simon, and saith unto him, We have found the Messias, which is, being interpreted, the Christ.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 42,
    text: "And he brought him to Jesus. And when Jesus beheld him, he said, Thou art Simon the son of Jona: thou shalt be called Cephas, which is by interpretation, A stone.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 43,
    text: "The day following Jesus would go forth into Galilee, and findeth Philip, and saith unto him, Follow me.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 44,
    text: "Now Philip was of Bethsaida, the city of Andrew and Peter.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 45,
    text: "Philip findeth Nathanael, and saith unto him, We have found him, of whom Moses in the law, and the prophets, did write, Jesus of Nazareth, the son of Joseph.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 46,
    text: "And Nathanael said unto him, Can there any good thing come out of Nazareth? Philip saith unto him, Come and see.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 47,
    text: "Jesus saw Nathanael coming to him, and saith of him, Behold an Israelite indeed, in whom is no guile!",
  },
  {
    book: 43,
    chapter: 1,
    verse: 48,
    text: "Nathanael saith unto him, Whence knowest thou me? Jesus answered and said unto him, Before that Philip called thee, when thou wast under the fig tree, I saw thee.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 49,
    text: "Nathanael answered and saith unto him, Rabbi, thou art the Son of God; thou art the King of Israel.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 50,
    text: "Jesus answered and said unto him, Because I said unto thee, I saw thee under the fig tree, believest thou? thou shalt see greater things than these.",
  },
  {
    book: 43,
    chapter: 1,
    verse: 51,
    text: "And he saith unto him, Verily, verily, I say unto you, Hereafter ye shall see heaven open, and the angels of God ascending and descending upon the Son of man.",
  },

  // ── John 2 ─────────────────────────────────────────────────────────────
  {
    book: 43,
    chapter: 2,
    verse: 1,
    text: "And the third day there was a marriage in Cana of Galilee; and the mother of Jesus was there:",
  },
  {
    book: 43,
    chapter: 2,
    verse: 2,
    text: "And both Jesus was called, and his disciples, to the marriage.",
  },
  {
    book: 43,
    chapter: 2,
    verse: 3,
    text: "And when they wanted wine, the mother of Jesus saith unto him, They have no wine.",
  },
  {
    book: 43,
    chapter: 2,
    verse: 4,
    text: "Jesus saith unto her, Woman, what have I to do with thee? mine hour is not yet come.",
  },
  {
    book: 43,
    chapter: 2,
    verse: 5,
    text: "His mother saith unto the servants, Whatsoever he saith unto you, do it.",
  },
  {
    book: 43,
    chapter: 2,
    verse: 6,
    text: "And there were set there six waterpots of stone, after the manner of the purifying of the Jews, containing two or three firkins apiece.",
  },
  {
    book: 43,
    chapter: 2,
    verse: 7,
    text: "Jesus saith unto them, Fill the waterpots with water. And they filled them up to the brim.",
  },
  {
    book: 43,
    chapter: 2,
    verse: 8,
    text: "And he saith unto them, Draw out now, and bear unto the governor of the feast. And they bare it.",
  },
  {
    book: 43,
    chapter: 2,
    verse: 9,
    text: "When the ruler of the feast had tasted the water that was made wine, and knew not whence it was: (but the servants which drew the water knew;) the governor of the feast called the bridegroom,",
  },
  {
    book: 43,
    chapter: 2,
    verse: 10,
    text: "And saith unto him, Every man at the beginning doth set forth good wine; and when men have well drunk, then that which is worse: but thou hast kept the good wine until now.",
  },
  {
    book: 43,
    chapter: 2,
    verse: 11,
    text: "This beginning of miracles did Jesus in Cana of Galilee, and manifested forth his glory; and his disciples believed on him.",
  },
  {
    book: 43,
    chapter: 2,
    verse: 12,
    text: "After this he went down to Capernaum, he, and his mother, and his brethren, and his disciples: and they continued there not many days.",
  },
  {
    book: 43,
    chapter: 2,
    verse: 13,
    text: "And the Jews' passover was at hand, and Jesus went up to Jerusalem,",
  },
  {
    book: 43,
    chapter: 2,
    verse: 14,
    text: "And found in the temple those that sold oxen and sheep and doves, and the changers of money sitting:",
  },
  {
    book: 43,
    chapter: 2,
    verse: 15,
    text: "And when he had made a scourge of small cords, he drove them all out of the temple, and the sheep, and the oxen; and poured out the changers' money, and overthrew the tables;",
  },
  {
    book: 43,
    chapter: 2,
    verse: 16,
    text: "And said unto them that sold doves, Take these things hence; make not my Father's house an house of merchandise.",
  },
  {
    book: 43,
    chapter: 2,
    verse: 17,
    text: "And his disciples remembered that it was written, The zeal of thine house hath eaten me up.",
  },
  {
    book: 43,
    chapter: 2,
    verse: 18,
    text: "Then answered the Jews and said unto him, What sign shewest thou unto us, seeing that thou doest these things?",
  },
  {
    book: 43,
    chapter: 2,
    verse: 19,
    text: "Jesus answered and said unto them, Destroy this temple, and in three days I will raise it up.",
  },
  {
    book: 43,
    chapter: 2,
    verse: 20,
    text: "Then said the Jews, Forty and six years was this temple in building, and wilt thou rear it up in three days?",
  },
  { book: 43, chapter: 2, verse: 21, text: "But he spake of the temple of his body." },
  {
    book: 43,
    chapter: 2,
    verse: 22,
    text: "When therefore he was risen from the dead, his disciples remembered that he had said this unto them; and they believed the scripture, and the word which Jesus had said.",
  },
  {
    book: 43,
    chapter: 2,
    verse: 23,
    text: "Now when he was in Jerusalem at the passover, in the feast day, many believed in his name, when they saw the miracles which he did.",
  },
  {
    book: 43,
    chapter: 2,
    verse: 24,
    text: "But Jesus did not commit himself unto them, because he knew all men,",
  },
  {
    book: 43,
    chapter: 2,
    verse: 25,
    text: "And needed not that any should testify of man: for he knew what was in man.",
  },

  // ── John 3 ─────────────────────────────────────────────────────────────
  {
    book: 43,
    chapter: 3,
    verse: 1,
    text: "There was a man of the Pharisees, named Nicodemus, a ruler of the Jews:",
  },
  {
    book: 43,
    chapter: 3,
    verse: 2,
    text: "The same came to Jesus by night, and said unto him, Rabbi, we know that thou art a teacher come from God: for no man can do these miracles that thou doest, except God be with him.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 3,
    text: "Jesus answered and said unto him, Verily, verily, I say unto thee, Except a man be born again, he cannot see the kingdom of God.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 4,
    text: "Nicodemus saith unto him, How can a man be born when he is old? can he enter the second time into his mother's womb, and be born?",
  },
  {
    book: 43,
    chapter: 3,
    verse: 5,
    text: "Jesus answered, Verily, verily, I say unto thee, Except a man be born of water and of the Spirit, he cannot enter into the kingdom of God.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 6,
    text: "That which is born of the flesh is flesh; and that which is born of the Spirit is spirit.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 7,
    text: "Marvel not that I said unto thee, Ye must be born again.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 8,
    text: "The wind bloweth where it listeth, and thou hearest the sound thereof, but canst not tell whence it cometh, and whither it goeth: so is every one that is born of the Spirit.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 9,
    text: "Nicodemus answered and said unto him, How can these things be?",
  },
  {
    book: 43,
    chapter: 3,
    verse: 10,
    text: "Jesus answered and said unto him, Art thou a master of Israel, and knowest not these things?",
  },
  {
    book: 43,
    chapter: 3,
    verse: 11,
    text: "Verily, verily, I say unto thee, We speak that we do know, and testify that we have seen; and ye receive not our witness.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 12,
    text: "If I have told you earthly things, and ye believe not, how shall ye believe, if I tell you of heavenly things?",
  },
  {
    book: 43,
    chapter: 3,
    verse: 13,
    text: "And no man hath ascended up to heaven, but he that came down from heaven, even the Son of man which is in heaven.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 14,
    text: "And as Moses lifted up the serpent in the wilderness, even so must the Son of man be lifted up:",
  },
  {
    book: 43,
    chapter: 3,
    verse: 15,
    text: "That whosoever believeth in him should not perish, but have eternal life.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 16,
    text: "For God so loved the world, that he gave his only begotten Son, that whosoever believeth in him should not perish, but have everlasting life.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 17,
    text: "For God sent not his Son into the world to condemn the world; but that the world through him might be saved.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 18,
    text: "He that believeth on him is not condemned: but he that believeth not is condemned already, because he hath not believed in the name of the only begotten Son of God.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 19,
    text: "And this is the condemnation, that light is come into the world, and men loved darkness rather than light, because their deeds were evil.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 20,
    text: "For every one that doeth evil hateth the light, neither cometh to the light, lest his deeds should be reproved.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 21,
    text: "But he that doeth truth cometh to the light, that his deeds may be made manifest, that they are wrought in God.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 22,
    text: "After these things came Jesus and his disciples into the land of Judaea; and there he tarried with them, and baptized.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 23,
    text: "And John also was baptizing in Aenon near to Salim, because there was much water there: and they came, and were baptized.",
  },
  { book: 43, chapter: 3, verse: 24, text: "For John was not yet cast into prison." },
  {
    book: 43,
    chapter: 3,
    verse: 25,
    text: "Then there arose a question between some of John's disciples and the Jews about purifying.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 26,
    text: "And they came unto John, and said unto him, Rabbi, he that was with thee beyond Jordan, to whom thou barest witness, behold, the same baptizeth, and all men come to him.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 27,
    text: "John answered and said, A man can receive nothing, except it be given him from heaven.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 28,
    text: "Ye yourselves bear me witness, that I said, I am not the Christ, but that I am sent before him.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 29,
    text: "He that hath the bride is the bridegroom: but the friend of the bridegroom, which standeth and heareth him, rejoiceth greatly because of the bridegroom's voice: this my joy therefore is fulfilled.",
  },
  { book: 43, chapter: 3, verse: 30, text: "He must increase, but I must decrease." },
  {
    book: 43,
    chapter: 3,
    verse: 31,
    text: "He that cometh from above is above all: he that is of the earth is earthly, and speaketh of the earth: he that cometh from heaven is above all.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 32,
    text: "And what he hath seen and heard, that he testifieth; and no man receiveth his testimony.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 33,
    text: "He that hath received his testimony hath set to his seal that God is true.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 34,
    text: "For he whom God hath sent speaketh the words of God: for God giveth not the Spirit by measure unto him.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 35,
    text: "The Father loveth the Son, and hath given all things into his hand.",
  },
  {
    book: 43,
    chapter: 3,
    verse: 36,
    text: "He that believeth on the Son hath everlasting life: and he that believeth not the Son shall not see life; but the wrath of God abideth on him.",
  },
];
